<template>
    <div class="relative" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <!-- Error state -->
        <div
            class="flex w-full items-center gap-3 px-5 py-2"
            data-testid="audio-player-error"
            v-if="showErrorState && hasError"
        >
            <div
                class="flex shrink-0 items-center justify-center rounded-full bg-red-100 size-[36px]"
            >
                <icon name="regular/alert-circle" width="20px" class="text-red-500" />
            </div>
            <div class="grow min-w-0">
                <p class="m-0 text-sm font-medium text-red-500">
                    {{ $t('general.audioPlayerError.title') }}
                </p>
                <p class="m-0 text-sm text-gray-500">
                    {{ $t('general.audioPlayerError.description') }}
                </p>
            </div>
            <button
                class="flex shrink-0 items-center gap-1 text-sm font-bold text-blue-500 hover:text-blue-700"
                data-testid="audio-player-retry"
                @click="retry"
            >
                <icon name="bold/synchronize-arrow-1" width="14px" />
                {{ $t('general.audioPlayerError.retry') }}
            </button>
        </div>

        <!-- Normal player -->
        <template v-else>
            <div
                class="absolute w-full px-5 space-x-2 py-2 z-10 rounded-lg | hover:bg-gray-750 | hover:bg-opacity-50"
                v-if="isExternalLink && hover"
            >
                <a
                    class="relative left-1/2 transform -translate-x-1/2 inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded shadow-sm text-sm text-gray-700 cursor-pointer | hover:bg-gray-100 hover:text-gray-800"
                    :href="externalLinkUrl"
                    target="_blank"
                >
                    {{ $t(externalLinkText) }}
                </a>
            </div>

            <div
                class="flex items-center px-5 space-x-2 py-2 bg-gray-150 overflow-y-hidden h-full | 2xl:space-x-3 | transition-width duration-300 | hover:bg-gray-300"
                :class="[{ 'rounded-lg': !editing }, waveformClasses]"
                data-audio-player-body
            >
                <button
                    class="relative bg-blue-500 shadow-md rounded-full | transition duration-200 | hover:bg-blue-600"
                    @click="playPause"
                >
                    <div class="h-10 w-10 flex items-center justify-center">
                        <activix-spinner
                            :size="18"
                            :line-size="2"
                            v-if="loading && !placeholder && !editing"
                        />
                        <icon
                            :name="iconIsPlaying"
                            class="text-sm text-white"
                            :class="{ '-mr-0.5': !isPlaying }"
                            v-else
                        />
                    </div>
                </button>

                <wave-surfer
                    :url="effectiveUrl"
                    :duration="file.duration"
                    :placeholder="placeholder"
                    :autoload="editing"
                    :autoplay="!editing"
                    ref="wavesurfer"
                    @ready="setReady"
                    @time="setTime"
                    @playing="setPlaying"
                    @error="onLoadError"
                    v-model:loading="loading"
                />

                <div class="tracking-wider" v-text="currentTime" />

                <activix-tooltip :content="$t('general.download')" v-if="downloadable">
                    <a
                        class="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded shadow-sm text-gray-700 cursor-pointer | hover:bg-gray-100 hover:text-gray-800 hover:border-gray-300 | focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                        data-testid="audio-player-download"
                        :href="effectiveUrl || undefined"
                        referrerpolicy="origin"
                        target="_blank"
                        @mouseenter="prefetchEffectiveUrl"
                        @focus="prefetchEffectiveUrl"
                        @click="onDownloadClick"
                    >
                        <icon name="regular/download-button" />
                    </a>
                </activix-tooltip>
            </div>
        </template>
    </div>
</template>

<script>
    import { isSignedUrlExpired } from '@/plugins/signedUrl/cache.ts';
    import WaveSurfer from './WaveSurfer.vue';

    export default {
        components: {
            WaveSurfer,
        },

        props: {
            action: {
                type: Object,
                default: null,
            },
            file: {
                type: Object,
                required: true,
                duration: null,
            },
            downloadable: {
                type: Boolean,
                default: false,
            },
            editing: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: Boolean,
                default: false,
            },
            // Optional callback to fetch a fresh playable URL on demand.
            // When provided, the player will resolve `file.url` via this
            // function the first time the user clicks play or download.
            // Useful for short-lived signed URLs whose TTL must start at
            // play time, not page load.
            resolveUrl: {
                type: Function,
                default: null,
            },
            showErrorState: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                isPlaying: false,
                wavesurferReady: false,
                currentTime: '00:00',
                loading: false,
                hover: false,
                resolvedUrl: null,
                resolvingUrlPromise: null,
                hasError: false,
            };
        },

        computed: {
            iconIsPlaying() {
                return this.isPlaying ? 'bold/controls-pause' : 'bold/arrow-button-right-2';
            },

            waveformClasses() {
                if (!this.placeholder && !this.wavesurferReady && !this.editing) {
                    return 'w-32';
                }

                if (!this.wavesurferReady && !this.editing) {
                    return 'w-auto';
                }

                return 'w-full';
            },

            isExternalLink() {
                return this.action && this.action.type == 'externalLink';
            },

            externalLinkText() {
                return this.action.text;
            },

            externalLinkUrl() {
                return this.action.url;
            },

            // Effective URL passed to wave-surfer / download anchor.
            // Falls back to the file prop for callers that don't use
            // the resolveUrl strategy (legacy behaviour).
            // Returns null when a previously resolved URL has expired so
            // the next interaction triggers a fresh fetch.
            effectiveUrl() {
                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    return this.resolvedUrl;
                }

                if (this.resolveUrl) {
                    // On-demand mode: never expose a stale or unresolved
                    // signed URL via the anchor href.
                    return null;
                }

                return this.file.url || null;
            },
        },

        methods: {
            setPlaying(playing) {
                this.isPlaying = playing;
            },

            setReady(ready) {
                this.wavesurferReady = ready;
            },

            setTime(time) {
                this.currentTime = time;
            },

            // wave-surfer failed to load the source (broken URL, CORS, 404).
            // Surface the error UI only when the caller opted into it; legacy
            // callers keep the silent behaviour they had before.
            onLoadError() {
                this.loading = false;

                if (this.showErrorState) {
                    this.hasError = true;
                }
            },

            async resolveEffectiveUrl() {
                if (!this.resolveUrl) {
                    return this.effectiveUrl;
                }

                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    return this.resolvedUrl;
                }

                // Let an immediate play/download action await a hover/focus prefetch.
                if (this.resolvingUrlPromise) {
                    return this.resolvingUrlPromise;
                }

                this.resolvingUrlPromise = (async () => {
                    try {
                        this.resolvedUrl = await this.resolveUrl();
                        return this.resolvedUrl;
                    } catch (error) {
                        if (this.showErrorState) {
                            this.hasError = true;
                        } else {
                            this.$notify?.error?.(this.$t('general.error'));
                        }
                        throw error;
                    } finally {
                        this.resolvingUrlPromise = null;
                    }
                })();

                return this.resolvingUrlPromise;
            },

            // Returns true when the resolver mode requires the player to
            // fetch (or re-fetch) a fresh signed URL before consuming it.
            needsResolvedUrl() {
                if (!this.resolveUrl) {
                    return false;
                }

                return !this.resolvedUrl || isSignedUrlExpired(this.resolvedUrl);
            },

            async playPause() {
                if (!this.$refs.wavesurfer) {
                    return;
                }

                if (this.needsResolvedUrl() && !this.isPlaying) {
                    // Drop stale wave-surfer state so it reloads with the
                    // fresh URL once we have it.
                    if (isSignedUrlExpired(this.resolvedUrl)) {
                        this.resolvedUrl = null;
                        this.wavesurferReady = false;
                    }

                    await this.resolveEffectiveUrl();

                    if (!this.resolvedUrl) {
                        return;
                    }

                    await this.$nextTick();
                }

                this.$refs.wavesurfer.playPause();
            },

            // Pre-fetch the signed URL before the user actually clicks the
            // download anchor. We trigger this on hover or focus so the link
            // already has a real `href` by the time the click fires — no
            // need for window.open shenanigans that browsers may flag as
            // popups, and no "first click opens about:blank" UX glitch.
            async prefetchEffectiveUrl() {
                if (!this.needsResolvedUrl() || this.resolvingUrlPromise) {
                    return;
                }

                try {
                    await this.resolveEffectiveUrl();
                } catch (_) {
                    // resolveEffectiveUrl already surfaces the error via
                    // $notify; swallow here so a hover doesn't throw.
                }
            },

            async onDownloadClick(event) {
                if (!this.resolveUrl) {
                    // Legacy mode without the resolver — let the click
                    // navigate using the static href on the anchor.
                    return;
                }

                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    // Anchor already has a fresh URL — let the click navigate.
                    return;
                }

                // Either the URL is still being resolved, was never resolved,
                // or expired since the last interaction. Block navigation,
                // re-resolve, and redirect the current tab. We avoid
                // window.open() here because some browsers treat the
                // post-await call as outside the user gesture and either
                // block the popup or render about:blank until the assignment
                // lands.
                event.preventDefault();

                const url = await this.resolveEffectiveUrl();

                if (url) {
                    window.location.href = url;
                }
            },

            async retry() {
                this.hasError = false;
                this.resolvedUrl = null;
                this.wavesurferReady = false;

                // Clearing hasError swaps the error block back for the player,
                // which remounts a fresh wave-surfer. Wait for that remount,
                // then re-drive the load that previously failed.
                await this.$nextTick();

                this.playPause();
            },

            onMouseEnter() {
                this.hover = true;
            },

            onMouseLeave() {
                this.hover = false;
            },
        },
    };
</script>
